import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'

import type { WorkOrderEvidenceField } from './workOrderEvidence'
import { normalizeWorkOrderText } from './workOrderText'

export interface WorkOrderTextRun {
  text: string
  page: number
  box: NormalizedBBox
  contextBox?: NormalizedBBox
  field?: string
}

function spansFor<T>(items: T[], textOf: (item: T) => string) {
  let cursor = 0
  return items.map((item) => {
    const text = normalizeWorkOrderText(textOf(item))
    const start = cursor
    cursor += text.length + 1
    return { item, text, start, end: cursor - 1 }
  })
}

function matchingItems<T>(
  items: T[],
  textOf: (item: T) => string,
  query: string
): T[][] {
  const needle = normalizeWorkOrderText(query)
  if (!needle) return []
  const spans = spansFor(items, textOf)
  const haystack = spans.map((span) => span.text).join(' ')
  const hits: T[][] = []
  let offset = haystack.indexOf(needle)
  while (offset !== -1) {
    hits.push(
      spans
        .filter(
          (span) => span.start < offset + needle.length && span.end > offset
        )
        .map((span) => span.item)
    )
    offset = haystack.indexOf(needle, offset + needle.length)
  }
  return hits
}

export function findWorkOrderMatches(
  runs: WorkOrderTextRun[],
  query: string
): WorkOrderTextRun[][] {
  return matchingItems(runs, (run) => run.text, query)
}

export function findWorkOrderText(
  runs: WorkOrderTextRun[],
  query: string
): WorkOrderTextRun[] {
  return [...new Set(findWorkOrderMatches(runs, query).flat())]
}

export function locateWorkOrderCitation(
  runs: WorkOrderTextRun[],
  evidence: WorkOrderEvidenceField[],
  quote: string
): WorkOrderTextRun[] {
  const direct = findWorkOrderText(runs, quote)
  if (direct.length) return direct
  const fields = matchingItems(evidence, (item) => item.text, quote).flat()
  const ids = new Set(fields.map((field) => field.id))
  return runs.filter((run) => run.field && ids.has(run.field))
}

export function locateCmmsCitation(
  runs: WorkOrderTextRun[],
  evidence: WorkOrderEvidenceField[],
  quote: string,
  nodeIds: string[]
): WorkOrderTextRun[] {
  const ids = new Set(nodeIds)
  const anchored = runs.filter((run) => run.field && ids.has(run.field))
  return locateWorkOrderCitation(
    anchored,
    evidence.filter((field) => ids.has(field.id)),
    quote
  )
}
